import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LeadScreenshotsViewSchema } from '@utazzo/shared';
import { toast } from 'sonner';
import { z } from 'zod';

import type { LeadScreenshotsView } from '@utazzo/shared';

import { fileToBase64 } from '@/features/ops-cases/file-base64';
import { apiClient } from '@/lib/api-client';
import { extractServerError } from '@/lib/api-error';

// Server contract: apps/api/src/modules/lead-screenshots/lead-screenshots.controller.ts

const UPLOAD_TIMEOUT_MS = 120_000;
const UrlSchema = z.object({ url: z.string(), expiresAt: z.coerce.date() });

const QK = {
  list: (leadId: string) => ['leads', 'screenshots', leadId] as const,
  url: (leadId: string, id: string) => ['leads', 'screenshots', leadId, 'url', id] as const,
};

export const useLeadScreenshots = (leadId: string) =>
  useQuery({
    queryKey: QK.list(leadId),
    queryFn: async (): Promise<LeadScreenshotsView> => {
      const { data } = await apiClient.get<unknown>(`/leads/${leadId}/screenshots`);
      return LeadScreenshotsViewSchema.parse(data);
    },
  });

/** Presigned view link (5 min on the server); refetched a little before it lapses. */
export const useLeadScreenshotUrl = (leadId: string, id: string) =>
  useQuery({
    queryKey: QK.url(leadId, id),
    queryFn: async () => {
      const { data } = await apiClient.get<unknown>(`/leads/${leadId}/screenshots/${id}/url`);
      return UrlSchema.parse(data);
    },
    staleTime: 4 * 60_000,
    refetchInterval: 4 * 60_000,
  });

/**
 * Uploads several images for one booking item. One request per file keeps
 * each body well under the API's 64MB JSON limit; the last response wins.
 * The note (if any) goes with every file of the batch.
 */
export const useUploadLeadScreenshots = (leadId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      componentId: string;
      files: File[];
      note?: string;
    }): Promise<LeadScreenshotsView> => {
      const note = args.note?.trim() || undefined;
      let view: LeadScreenshotsView | null = null;
      for (const file of args.files) {
        const contentBase64 = await fileToBase64(file);
        const { data } = await apiClient.post<unknown>(
          `/leads/${leadId}/screenshots`,
          {
            componentId: args.componentId,
            files: [{ fileName: file.name, contentBase64 }],
            ...(note ? { note } : {}),
          },
          { timeout: UPLOAD_TIMEOUT_MS },
        );
        view = LeadScreenshotsViewSchema.parse(data);
        qc.setQueryData(QK.list(leadId), view);
      }
      return view as LeadScreenshotsView;
    },
    onSuccess: (_view, args) =>
      toast.success(
        args.files.length > 1 ? `${args.files.length} screenshots uploaded` : 'Screenshot uploaded',
      ),
    onError: (err) => {
      void qc.invalidateQueries({ queryKey: QK.list(leadId) });
      toast.error(extractServerError(err).message ?? 'Could not upload the screenshot');
    },
  });
};

export const useDeleteLeadScreenshot = (leadId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<LeadScreenshotsView> => {
      const { data } = await apiClient.delete<unknown>(`/leads/${leadId}/screenshots/${id}`);
      return LeadScreenshotsViewSchema.parse(data);
    },
    onSuccess: (rows) => {
      qc.setQueryData(QK.list(leadId), rows);
      toast.success('Screenshot removed');
    },
    onError: (err) =>
      toast.error(extractServerError(err).message ?? 'Could not remove the screenshot'),
  });
};
